import {
  ControlledDateOnlyPicker,
  ControlledNumberField,
  ControlledSelect,
  FieldGrid,
  FormBody,
  FormContainerProvider,
  FormSubmitButton,
  useConfirmDialog,
  useNumberOptionEnum,
  useSnackbar,
} from '@if/mui'
import { Stack } from '@mui/material'
import { useGetLatestLoadLoansRun, useStartLoadLoans } from '@src/api/dev-tools/load-loans-api'
import { getETermList } from '@src/types/constants'
import { DevToolsCommandType } from '@src/types/dev-tools'
import { useTranslation } from 'react-i18next'
import { LoadLoansSchema, type LoadLoansDraft, type LoadLoansValues } from './load-loans-schema'
import CommandCard from './CommandCard'
import LoadLoansProgress from './LoadLoansProgress'

type LoadLoansOutcome = Promise<void | false>

/**
 * Generate N identical loans whose first payment falls on the same date (PAD load). The API builds a
 * template loan and the workflow clones it; the card shows the latest run, polled while it runs.
 */
const LoadLoansCard = () => {
  const { t } = useTranslation()
  const { showError } = useSnackbar()
  const startLoadLoans = useStartLoadLoans()
  const [latestRun] = useGetLatestLoadLoansRun()
  const termOptions = useNumberOptionEnum(getETermList())

  const confirm = useConfirmDialog<{ count: number }>({
    title: t('admin.devTools.dialog.title'),
    message: (ctx) => t('admin.devTools.commands.loadLoans.confirmationMessage', { nb: ctx.props.count }),
    confirmLabel: t('admin.devTools.commands.loadLoans.actionLabel'),
  })

  const handleSubmit = async (values: LoadLoansValues): LoadLoansOutcome => {
    if (!(await confirm.open({ count: values.count }))) return false
    try {
      await startLoadLoans.mutateAsync(values)
    } catch {
      showError(t('admin.devTools.messages.error'))
      return false
    }
  }

  return (
    <FormContainerProvider<LoadLoansDraft, unknown, LoadLoansOutcome, LoadLoansValues>
      mode="create"
      useFormSetup={() => ({ schema: LoadLoansSchema })}
      onSubmit={handleSubmit}
      successMessage={t('admin.devTools.messages.success')}
    >
      <CommandCard
        title={t('admin.devTools.commands.loadLoans.title')}
        description={t('admin.devTools.commands.loadLoans.description')}
        commandType={DevToolsCommandType.LoadLoans}
        actions={<FormSubmitButton label={t('admin.devTools.commands.loadLoans.actionLabel')} />}
      >
        <Stack spacing={2}>
          <FormBody<LoadLoansDraft, unknown, LoadLoansValues>>
            {({ control }) => (
              <FieldGrid columns={{ xs: 1, sm: 2 }}>
                <ControlledNumberField
                  control={control}
                  name="count"
                  label={t('admin.devTools.commands.loadLoans.count')}
                  disableDecimals
                  required
                />
                <ControlledSelect
                  control={control}
                  name="term"
                  label={t('loan.createWizard.term')}
                  options={termOptions}
                />
                <ControlledDateOnlyPicker
                  control={control}
                  name="activationPlannedOn"
                  label={t('loan.createWizard.activationDate')}
                />
                <ControlledDateOnlyPicker
                  control={control}
                  name="firstPaymentExpectedOn"
                  label={t('loan.createWizard.firstPaymentDate')}
                />
              </FieldGrid>
            )}
          </FormBody>
          {latestRun && <LoadLoansProgress run={latestRun} />}
        </Stack>
      </CommandCard>
    </FormContainerProvider>
  )
}

export default LoadLoansCard
